import React, { useMemo } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Navigation, A11y, Pagination, Mousewheel } from 'swiper'
import classNames from 'classnames'
import { MatchingFundHelper, GtmHelper, formatCents, findApiV4Link, gaEvent, useJsonApi } from 'shared'
import { useCallback } from 'react'
import { NeedsListProps, NeedProps, NeedsApiResult, ProjectApiResult } from './types'

SwiperCore.use([Navigation, A11y, Pagination, Mousewheel])

export const NeedList = ({ apiUrl, projectApiUrl }: NeedsListProps): JSX.Element | null => {
  const { data: needs } = useJsonApi<NeedsApiResult>(apiUrl)
  const { data: project } = useJsonApi<ProjectApiResult>(projectApiUrl)
  if (!needs || !project) return null

  const count = needs.total_entries
  const btnGroupClass = count < 4 ? ' desktop:hidden' : ' desktop:block'

  if (count === 0) return null

  return (
    <div className="relative">
      <h2 className="text-3xl text-center mb-4 desktop:mb-8 hidden-for-ab-test-feb-2022">
        {I18n.t('projects.show.needs.headline')} ({count})
      </h2>
      <div className={classNames('btn-group btn-group-with-small-arrows hidden', btnGroupClass)}>
        <a id="need-list-prev" className="btn btn-secondary">
          <i className="fa fa-caret-left"></i>
        </a>
        <a id="need-list-next" className="btn btn-secondary">
          <i className="fa fa-caret-right"></i>
        </a>
      </div>

      {count === 1 ? (
        <div className="mb-5 desktop:w-1/3 desktop:px-3 desktop:mx-auto">
          <Need project={project} need={needs.data[0]} />
        </div>
      ) : (
        <div className="generic-teaser-swiper select-none mb-5">
          <Swiper
            spaceBetween={36}
            slidesPerView={1}
            pagination={{
              clickable: count > 1,
              dynamicBullets: count > 5 ? true : false,
            }}
            navigation={{
              nextEl: '#need-list-next',
              prevEl: '#need-list-prev',
              disabledClass: 'is-disabled',
            }}
            centeredSlides={count == 1}
            roundLengths
            mousewheel={{ forceToAxis: true }}
            breakpoints={{
              450: {
                slidesPerView: 3,
              },
            }}
          >
            {needs.data.map((need) => (
              <SwiperSlide key={need.id}>
                <Need project={project} need={need} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </div>
  )
}

const useDonateUrl = (project: NeedProps['project'], need: NeedProps['need']): string | undefined => {
  return useMemo(
    () => (!project.donations_prohibited ? findApiV4Link({ ofObject: need, withRel: 'new_donation' }) : undefined),
    [project.donations_prohibited, need]
  )
}

const Need = ({ project, need }: NeedProps): JSX.Element => {
  const donateUrl = useDonateUrl(project, need)

  const hasMatchingFund = !!MatchingFundHelper.locallyActiveMatchingFund(project)

  const setTracking: React.MouseEventHandler<HTMLAnchorElement> = useCallback(
    (event) => {
      if (!donateUrl) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      project && GtmHelper.pushAddToBasket(project)
      const tracking = (event.target as HTMLAnchorElement).classList.contains('btn')
        ? 'click need_donation_button'
        : 'click need_donation_container'
      gaEvent('donation', tracking, need.title)
    },
    [need.title, project, donateUrl]
  )

  const teaserClass = classNames('need-teaser', 'swiper-slide', {
    'need-teaser-disabled': project && project.donations_prohibited,
  })

  return (
    <a href={donateUrl} className={teaserClass} onClick={setTracking}>
      <div className="text-lg" dangerouslySetInnerHTML={{ __html: need.title }}></div>
      <div className="need-teaser-amount">
        {formatCents(need.open_amount_in_cents, { precision: 0 })}&nbsp;
        {I18n.t('projects.show.needs.still_missing_label')}&nbsp;
        {formatCents(need.requested_amount_in_cents, { precision: 0 })}
      </div>
      <div className="need-teaser-description" dangerouslySetInnerHTML={{ __html: need.description }}></div>
      <DonateButton duplicationIcon={hasMatchingFund} />
      <div className="progress generic-progress-bar">
        <div className="bar" style={{ width: `${need.progress_percentage}%` }}></div>
      </div>
    </a>
  )
}

const DonateButton = ({ duplicationIcon }: { duplicationIcon?: boolean }): JSX.Element => {
  const btnClass = classNames('btn', 'btn-primary', 'btn-large', 'no-pseudo-hover-styles', {
    'duplication-icon': duplicationIcon,
  })

  return <span className={btnClass}>{I18n.t('projects.show.needs.donate_button_label')}</span>
}
